import { animate, fadeIn, flyAbove, flyBelow } from '@lit-labs/motion'
import { consume, createContext, provide } from '@lit/context'
import { SchmancyElement } from '@schmancy/mixins/index'
import hashContent from '@schmancy/utils/hashContent'
import { html } from 'lit'
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js'
import { cache } from 'lit/directives/cache.js'
import { timer } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

export const delayContext = createContext<number>('delay-context')

@customElement('schmancy-delay')
export class SchmancyDelay extends SchmancyElement {
	@property({ type: Number, reflect: true }) delay = 0
	@property({ type: String }) motion = 'flyBelow'
	@state() private rendered = false
	@consume({ context: delayContext, subscribe: true }) parentDelay = 0
	@provide({ context: delayContext }) effectiveDelay = 0

	@property({ type: Boolean }) once? = true

	private sessionKey = ''
	private mutationObserver?: MutationObserver

	@queryAssignedElements({
		flatten: true,
	})
	assignedElements: HTMLElement[]
	firstUpdated() {
		this.observeSlotChanges()
		this.updateRenderState()
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.mutationObserver?.disconnect()
	}

	private observeSlotChanges() {
		const slot = this.shadowRoot?.querySelector('slot')
		if (!slot) return

		this.mutationObserver = new MutationObserver(() => {
			this.sessionKey = this.generateSessionKey()
		})

		this.mutationObserver.observe(slot, { childList: true, subtree: true })
	}

	private getTotalSiblingDelay(element: HTMLElement | null): number {
		if (!element || !(element instanceof HTMLElement)) return 0

		let totalDelay = 0
		let sibling = element.previousElementSibling

		while (sibling) {
			if (sibling instanceof SchmancyDelay) {
				totalDelay += sibling.delay
			}
			sibling = sibling.previousElementSibling
		}

		if (element.parentElement) {
			totalDelay += this.getTotalSiblingDelay(element.parentElement)
		}

		return totalDelay
	}

	private updateRenderState() {
		this.sessionKey = this.generateSessionKey()

		if (this.once && sessionStorage.getItem(this.sessionKey) === 'true') {
			this.rendered = true
			return
		}

		const siblingDelay = this.getTotalSiblingDelay(this)
		this.effectiveDelay = this.delay + this.parentDelay + siblingDelay

		timer(this.effectiveDelay)
			.pipe(takeUntil(this.disconnecting))
			.subscribe(() => {
				this.rendered = true
				if (this.once) {
					try {
						sessionStorage.setItem(this.sessionKey, 'true')
					} catch (error) {
						console.error('Error saving to session storage:', error)
					}
				}
			})
	}

	private generateSessionKey(): string {
		const slotContent = this.assignedElements.map(el => el.outerHTML).join('')
		return this.once ? hashContent(slotContent) : ''
	}

	private get motionLit(): any {
		return this.motion === 'flyBelow' ? flyBelow : this.motion === 'flyAbove' ? flyAbove : fadeIn
	}

	render() {
		return cache(
			this.rendered
				? html`<div
						${animate({
							in: this.motionLit,
							keyframeOptions: { duration: 300, easing: 'ease-out' },
						})}
					>
						<slot></slot>
					</div>`
				: html`
						<section class="hidden">
							<slot></slot>
						</section>
					`,
		)
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-delay': SchmancyDelay
	}
}
